import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import SpotPattern from '@mlb/react-patterns/components/Spot';
import Skeleton from '@mlb/react-patterns/components/Skeleton';
import Icon from '@mlb/react-patterns/components/Icon/icon.tsx';
import COLORS from '@mlb/react-patterns/style/universal.color';

import { useImageUtils } from '../../hooks';
import Card from '../AspectRatioCard';
import ScalableTextComponent from '../ScalableText';

const BASE_WIDTH = 344;
const BASE_HEIGHT = 120;
const ASPECT_RATIO = `${BASE_WIDTH}:${BASE_HEIGHT}`;
const LOGO_BASE_WIDTH = 56;
const ARROW_BASE_WIDTH = 40;
const NAME_BASE_WIDTH = 184;
const BASE_MARGIN = 16;

const Wrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: ${BASE_MARGIN * 100 / BASE_WIDTH}%;
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    background: rgba(18, 18, 18, 0.9);
`;

const TeamLogo = styled(SpotPattern)`
    flex: 0 0 ${LOGO_BASE_WIDTH * 100 / BASE_WIDTH}%;
    height: auto;
`;

const ScalableText = styled(ScalableTextComponent)`
    flex: 0 0 ${NAME_BASE_WIDTH * 100 / BASE_WIDTH}%;
`;

const TeamName = styled.span`
    display: flex;
    align-items: center;
    font-weight: 800;
    height: 100%;
    box-sizing: border-box;
    line-height: 1.1;
`;

const BackgroundImage = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ArrowRightCircle = styled(Icon)`
    width: ${ARROW_BASE_WIDTH * 100 / BASE_WIDTH}%;
    flex: 0 0 ${ARROW_BASE_WIDTH * 100 / BASE_WIDTH}%;
    height: auto;
    stroke: none;
`;

const FavoriteTeamCard = (props) => {

    const {
        id,
        name,
        imageWidths,
        imageTemplateUrl: templateUrl,
        imgSizes,
        loading,
        ...otherProps
    } = props;

    const { formatImageUrl } = useImageUtils();

    const imgSrc = formatImageUrl({
        templateUrl,
        width: imageWidths[imageWidths.length - 1],
        aspectRatio: ASPECT_RATIO,
    });
    const imgSrcSet = imageWidths.map((width) => `${formatImageUrl({
        templateUrl,
        width,
        aspectRatio: ASPECT_RATIO,
    })} ${width}w`).join(', ');

    return (
        <Card
            aspectRatio={ASPECT_RATIO}
            {...otherProps}
        >
            { loading ? (
                <Skeleton margin={0} />
            ) : (
                <>
                    <BackgroundImage
                        src={imgSrc}
                        srcSet={imgSrcSet}
                        sizes={imgSizes}
                        loading={'lazy'}
                    />
                    <Wrapper>
                        <TeamLogo
                            id={id}
                            lazyload
                            width={LOGO_BASE_WIDTH}
                            type={'team'}
                            alt={name}
                        />
                        <ScalableText
                            viewBoxWidth={NAME_BASE_WIDTH}
                            viewBoxHeight={BASE_HEIGHT}
                            fontSize="22px"
                        >
                            <TeamName>{name}</TeamName>
                        </ScalableText>
                        <ArrowRightCircle
                            name={'arrow-right-circle'}
                            fill={COLORS.mlbWhite}
                            width={ARROW_BASE_WIDTH}
                        />
                    </Wrapper>
                </>
            )}
        </Card>
    );

};

FavoriteTeamCard.propTypes = {
    id: PropTypes.number,
    name: PropTypes.string,
    imageTemplateUrl: PropTypes.string,
    imageWidths: PropTypes.array,
    imgSizes: PropTypes.string,
    loading: PropTypes.bool,
};

FavoriteTeamCard.defaultProps = {
    id: -1,
    name: '',
    loading: false,
    imageWidths: [
        372,
        640,
        1024,
    ],
};

export default FavoriteTeamCard;
